import { useEffect, useState } from "react";
import DeliveryChannelCard from "./DeliveryChannelCard";
import MailSendsPanel from "./MailSendsPanel";
import {
  DeliveryService,
  type LaunchDeliveryConfig,
  type MailEstimate,
  type PostalAddress,
  type ProviderAccount,
} from "@services/delivery/deliveryService";

/*
    Switching direct mail on for a campaign.

    It sits beside Publishers rather than inside it, because mail is not a publisher: there is
    no ad account, no bidding and no template. What it needs instead is whose Lob account pays
    for the postage and what return address goes on the card, and neither has an equivalent on
    a network row.

    Switching it on is refused until both are set. Refusing here is the point: the person who
    ticked the box is still sitting here and can fix it, while a publish job failing at four in
    the morning is read by nobody.
*/

const CHANNEL = "direct_mail";

const EMPTY: PostalAddress = {
  name: "",
  address_line1: "",
  address_line2: "",
  address_city: "",
  address_state: "",
  address_zip: "",
};

interface DirectMailCardProps {
  launchId: number;
  organizationId?: number | null;
}

export default function DirectMailCard({ launchId, organizationId }: DirectMailCardProps) {
  const [config, setConfig] = useState<LaunchDeliveryConfig | null>(null);
  const [accounts, setAccounts] = useState<ProviderAccount[]>([]);
  const [address, setAddress] = useState<PostalAddress>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [estimate, setEstimate] = useState<MailEstimate | null>(null);
  /** Set when somebody chooses to type a per-campaign address over the inherited one. */
  const [overriding, setOverriding] = useState(false);

  const service = new DeliveryService();

  /*
      What publishing would actually post, refreshed whenever the setup changes.

      Mail is priced per person and cannot be recalled, so this is the one number somebody
      needs while they can still change their mind. It is computed by the same command the
      send's budget guard uses, so a publish is never refused with a figure that disagrees
      with what was on screen here.
  */
  useEffect(() => {
    let cancelled = false;
    service
      .mailEstimate(launchId)
      .then((e) => !cancelled && setEstimate(e))
      .catch(() => !cancelled && setEstimate(null));
    return () => {
      cancelled = true;
    };
  }, [launchId, config?.is_enabled, config?.provider_account_id, config?.min_days_between_pieces]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      service.listLaunchChannels(launchId),
      service.listAccounts(organizationId ?? undefined),
    ])
      .then(([channels, providerAccounts]) => {
        if (cancelled) return;
        const mail = channels.find((c) => c.channel === CHANNEL) ?? null;
        setConfig(mail);
        setAccounts(providerAccounts);
        if (mail?.from_address && Object.keys(mail.from_address).length) {
          setAddress({ ...EMPTY, ...(mail.from_address as unknown as PostalAddress) });
        }
      })
      .catch(() => !cancelled && setError("Could not load direct mail settings."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [launchId, organizationId]);

  const save = async (patch: {
    provider_account_id?: number;
    from_address?: PostalAddress;
    min_days_between_pieces?: number;
  }) => {
    setBusy(true);
    setError(null);
    try {
      setConfig(await service.configureChannel(launchId, CHANNEL, patch));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the direct mail settings.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (nextEnabled: boolean) => {
    setBusy(true);
    setError(null);
    try {
      setConfig(await service.toggleChannel(launchId, CHANNEL, nextEnabled));
    } catch (e) {
      // The server refuses an incomplete channel and says what is missing. That message is
      // more useful than anything this component could invent.
      setError(e instanceof Error ? e.message : "Could not change direct mail.");
    } finally {
      setBusy(false);
    }
  };

  /*
      The organisation's default return address.

      It is the same six fields for every campaign a client ever runs, so typing them per
      campaign is both tedious and a way to post a typo. The account holds the default and
      the campaign overrides it only when a particular send needs a different address, which
      is how email already worked.
  */
  const account = accounts.find((a) => a.id === config?.provider_account_id) ?? null;
  const orgAddress = account?.from_address ?? null;
  const hasOrgAddress = !!orgAddress && !!orgAddress.address_line1;
  const ownAddress = !!config?.from_address && !!config.from_address.address_line1;
  // Whose address is actually going on the card, decided by the server so this cannot
  // disagree with what gets printed.
  const source = config?.sender_address_source ?? "none";
  const printed = config?.sender_address ?? null;
  // Only an address actually coming from somewhere else is "inherited". With nothing
  // anywhere the fields belong on screen -- hiding them behind a summary of an address that
  // does not exist leaves nowhere to type one.
  const inherited =
    (source === "organization" || source === "platform") && !ownAddress && !overriding;
  // Ours is a floor, not a recommendation: returned post goes to whoever is on the card, and
  // a client's recipients expect to see the client. So it is stated rather than assumed.
  const usingOurs = inherited && source === "platform";

  const saveOrgAddress = async () => {
    if (!account) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await service.updateAccount(account.id, { from_address: address });
      setOverriding(false);
      setAccounts((rows) => rows.map((a) => (a.id === updated.id ? updated : a)));
      // Cleared on the campaign so it inherits from here on. Leaving a copy behind means the
      // next edit to the organisation's address silently fails to reach this campaign.
      setConfig(await service.configureChannel(launchId, CHANNEL, { from_address: EMPTY }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the organization's address.");
    } finally {
      setBusy(false);
    }
  };

  const useOrgAddress = async () => {
    if (!orgAddress) return;
    setAddress({ ...EMPTY, ...(orgAddress as unknown as PostalAddress) });
    setOverriding(false);
    await save({ from_address: EMPTY });
  };

  if (loading) return null;

  const enabled = !!config?.is_enabled;
  const ready = !!config?.is_ready;
  const addressField = (key: keyof PostalAddress, label: string, width = 150) => (
    <div style={{ minWidth: width, flex: "1 1 auto" }}>
      <label className="form-label small text-muted mb-1" htmlFor={`mail-from-${key}`}>
        {label}
      </label>
      <input
        id={`mail-from-${key}`}
        className="form-control form-control-sm"
        value={address[key] ?? ""}
        onChange={(e) => setAddress({ ...address, [key]: e.target.value })}
        onBlur={() => save({ from_address: address })}
      />
    </div>
  );

  return (
    <>
      <DeliveryChannelCard
        label="Direct Mail"
        enabled={enabled}
        error={error}
        icon="bi-envelope"
        description="Post a printed card to everyone in this audience with an address."
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="fw-semibold">
              <i className="bi bi-envelope me-2" style={{ color: "#5b3df5" }}></i>
              One postcard per person
            </div>
            <div className="text-muted small">
              Sent when this campaign publishes, to everyone with an address who is not in the
              holdout, on the days the piece is scheduled to run.
            </div>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="direct-mail-toggle"
              aria-label="Direct mail"
              checked={enabled}
              disabled={busy || (!enabled && !ready)}
              onChange={(e) => toggle(e.target.checked)}
            />
          </div>
        </div>

        {!ready && (
          <div className="alert alert-warning py-2 small mb-3">
            {config?.is_ready === false && hasOrgAddress
              ? "Pick an account before switching mail on."
              : "Pick an account and a return address before switching mail on. Save the address for every campaign and you will not be asked again."}
          </div>
        )}

        <div className="mb-3">
          <label className="form-label small text-muted mb-1" htmlFor="mail-account">
            Account that pays for the postage
          </label>
          <select
            id="mail-account"
            className="form-select form-select-sm"
            value={config?.provider_account_id ?? ""}
            disabled={busy}
            onChange={(e) => save({ provider_account_id: Number(e.target.value) })}
          >
            <option value="">Choose an account…</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} ({account.key_environment ?? "no key"})
              </option>
            ))}
          </select>
          {accounts.length === 0 && (
            <div className="form-text">
              No delivery account for this organization yet. Add one in Delivery Setup.
            </div>
          )}
        </div>

        {estimate && <SendEstimate estimate={estimate} />}

        <div className="mb-3">
          <label className="form-label small text-muted mb-1" htmlFor="mail-quiet-period">
            Leave a person alone for
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              id="mail-quiet-period"
              type="number"
              min={0}
              className="form-control form-control-sm"
              style={{ width: 90 }}
              value={config?.min_days_between_pieces ?? 0}
              disabled={busy}
              onChange={(e) =>
                setConfig((c) => (c ? { ...c, min_days_between_pieces: Number(e.target.value) } : c))
              }
              onBlur={(e) => save({ min_days_between_pieces: Number(e.target.value) })}
            />
            <span className="small text-muted">days after a piece</span>
          </div>
          {/* The duplicate guard already stops the same piece going twice. This is about a
              second, different piece arriving the next morning, which no network has to
              worry about because showing an ad again is free and lands nowhere. */}
          <div className="form-text">
            Counts every campaign this client runs, not just this one. Zero means no gap.
          </div>
        </div>

        <div>
          <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
            <div className="small text-muted">
              Return address. It goes on every card, and returned post comes back to it.
            </div>
            {account && (
              <div className="d-flex gap-2">
                {inherited && !usingOurs ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={busy}
                    onClick={() => {
                      setAddress({ ...EMPTY, ...(orgAddress as unknown as PostalAddress) });
                      setOverriding(true);
                    }}
                    title="Type a different address for this campaign only"
                  >
                    Use a different address here
                  </button>
                ) : (
                  <>
                    {hasOrgAddress && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={busy}
                        onClick={useOrgAddress}
                      >
                        Use {account.name}&apos;s address
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      disabled={busy || !address.address_line1}
                      onClick={saveOrgAddress}
                      title="Every campaign on this organization will use it unless it sets its own"
                    >
                      Save for every campaign
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {inherited ? (
            // Shown rather than dropped into the inputs, so it is obvious this address is
            // the organisation's and that editing it here would be a per-campaign override.
            <div className={`border rounded p-2 small ${usingOurs ? "border-warning bg-warning-subtle" : "bg-light"}`}>
              <div className="text-muted mb-1">
                {usingOurs
                  ? "No return address set, so cards go out under Motimatic's. Returned post comes to us, and recipients see an Oakland address."
                  : `Using ${account?.name}'s address, set for every campaign.`}
              </div>
              <div>
                {[
                  printed?.name,
                  printed?.address_line1,
                  printed?.address_line2,
                  [printed?.address_city, printed?.address_state, printed?.address_zip]
                    .filter(Boolean)
                    .join(" "),
                ]
                  .filter(Boolean)
                  .join(", ")}
              </div>
              {usingOurs && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-2"
                  disabled={busy}
                  onClick={() => setOverriding(true)}
                >
                  Use this client&apos;s address instead
                </button>
              )}
            </div>
          ) : (
            <div className="d-flex gap-2 flex-wrap">
              {addressField("name", "Name", 180)}
              {addressField("address_line1", "Address", 220)}
              {addressField("address_line2", "Suite", 110)}
              {addressField("address_city", "City")}
              {addressField("address_state", "State", 90)}
              {addressField("address_zip", "ZIP", 110)}
            </div>
          )}
        </div>
        {/* The batches this campaign has agreed to post. Only once mail is actually on:
            costing a send needs a finished setup, and offering it beforehand produces a
            refusal instead of a number. */}
        {enabled && (
          <MailSendsPanel launchId={launchId} configKey={config?.provider_account_id ?? null} />
        )}
      </DeliveryChannelCard>
    </>
  );
}

const count = (n: number) => n.toLocaleString();

/*
    The sentence someone needs before publishing: how many pieces, and how much.

    The exclusions are listed rather than silently subtracted. "3,100 people have no address"
    is the number that gets a better file out of a client, and it is invisible if all anyone
    sees is a total smaller than they expected.

    The holdout sits apart from the exclusions on purpose. Those people were deliberately not
    mailed so the campaign can be measured, which is a feature and not a shortfall.
*/
function SendEstimate({ estimate }: { estimate: MailEstimate }) {
  const { excluded } = estimate;
  const reasons = [
    { n: excluded.no_address, label: "no address on file" },
    { n: excluded.suppressed, label: "asked not to be mailed" },
    { n: excluded.recently_mailed, label: "mailed too recently" },
    { n: excluded.already_mailed, label: "already had this piece" },
  ].filter((r) => r.n > 0);

  const short = estimate.within_budget === false;

  return (
    <div className={`alert py-2 px-3 mb-3 ${short ? "alert-danger" : "alert-light border"}`}>
      <div className="fw-semibold">
        Publishing posts {count(estimate.pieces)} piece{estimate.pieces === 1 ? "" : "s"} for
        about ${Number(estimate.cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}
      </div>
      <div className="text-muted small">
        ${estimate.rate} each, {estimate.rate_source}. Mail cannot be recalled once it is sent.
      </div>

      {reasons.length > 0 && (
        <div className="small mt-2">
          Not mailed:{" "}
          {reasons.map((r, i) => (
            <span key={r.label}>
              {i > 0 && ", "}
              {count(r.n)} {r.label}
            </span>
          ))}
        </div>
      )}

      {estimate.holdout > 0 && (
        <div className="small text-muted mt-1">
          {count(estimate.holdout)} held out on purpose, so the campaign can be measured.
        </div>
      )}

      {short && (
        <div className="small mt-2">
          That is more than the ${Number(estimate.budget_remaining).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}{" "}
          this engagement has left, so publishing will be refused. Add budget or narrow the
          audience.
        </div>
      )}
    </div>
  );
}
